import React from "react";
import { CheckCircle2, XCircle, AlertCircle, Info } from 'lucide-react';

export type Severity = "Critical" | "High" | "Medium" | "Low" | "Error" | "Warning" | "Normal";
export interface AlarmSeverityTagProps {
    severity: Severity;
    size?: "small" | "large";
}

function getSeverityAccent(severity: Severity) {
    switch (severity) {
        case "Critical":
        case "High":
        case "Error":
            return { 
                color: "#ef4444", 
                icon: <XCircle size={12} />,
                text: severity === "Error" ? "Error" : severity === "High" ? "High" : "Critical"
            };
        case "Medium":
        case "Warning":
            return { 
                color: "#f59e0b", 
                icon: <AlertCircle size={12} />,
                text: severity === "Warning" ? "Warning" : "Medium"
            };
        case "Low":
            return { 
                color: "#3b82f6", 
                icon: <Info size={12} />,
                text: "Low"
            };
        case "Normal":
            return { 
                color: "#059669", 
                icon: <CheckCircle2 size={12} />,
                text: "Normal"
            };
        default:
            return { 
                color: "#9ca3af", 
                icon: <Info size={12} />,
                text: "Unknown"
            };
    }
}

const AlarmSeverityTag = ({
    severity,
    size = "small"
}: AlarmSeverityTagProps) => {
    const statusAccent = getSeverityAccent(severity);
    const fontSize = size === "large" ? "11px" : "10px";
    const padding = size === "large" ? "4px 10px" : "3px 8px";
    const iconSize = size === "large" ? "13px" : "12px";
    
    // Make success states more prominent with darker text color
    const isSuccess = severity === "Normal";
    const textColor = isSuccess ? "#047857" : statusAccent.color; // Darker green for better contrast

    return (
        <div style={{
            fontSize: fontSize,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: textColor,
            flexShrink: 0,
            padding: padding,
            borderRadius: "4px",
            backgroundColor: `${statusAccent.color}10`,
            border: `1px solid ${statusAccent.color}20`,
            display: "inline-flex",
            alignItems: "center",
            gap: "6px"
        }}>
            <span style={{ 
                color: statusAccent.color,
                display: "inline-flex",
                alignItems: "center"
            }}>
                {React.cloneElement(statusAccent.icon, { style: { fontSize: iconSize } })}
            </span>
            {statusAccent.text}
        </div>
    );
}

export default AlarmSeverityTag;